import axios from "axios";
import React, { useEffect, useState } from "react";
import { genericHeaders, serviceUrl } from "../../utils/apiConfig";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { setIsViewedEmployee } from "../../store/reducers/addEmployeeSlice";
import { useDispatch } from "react-redux";

const EmployeeData = () => {
  const [tenants, setTenants] = useState([]);
  const [error, setError] = useState({});
  const [selectedTenant, setSelectedTenant] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [employees, setEmployees] = useState([]);
  const [reportingManagers, setReportingManagers] = useState([]);
  const [department, setDepartment] = useState([]);
  const [designation, setDesignation] = useState([]);
  const [workLocation, setWorkLocation] = useState([]);
  const [gridLoader, setGridLoader] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getTenants = async () => {
    const url = `${serviceUrl}/api/tenant/tenants`;
    try {
      let response = await axios.get(url, { headers: genericHeaders() });
      if (response.status === 200) {
        let newData = response.data;
        setTenants(newData);
      }
    } catch (err) {
      let blocker = { ...error };
      blocker.tenantName = "Something Went Wrong";
      setError(blocker);
      console.error(err);
    }
  };

  const getEmployees = async () => {
    setGridLoader(true);
    const url = `${serviceUrl}/api/super/admin/employees/search`;
    const body = {
      tenantId: selectedTenant ?? "",
      searchString: searchValue,
      page: paginationModel.page,
      size: paginationModel.pageSize,
    };
    try {
      let response = await axios.post(url, body, {
        headers: genericHeaders(),
      });
      if (response.status === 200) {
        setGridLoader(false);
        let newData = response.data;
        setEmployees(newData);
      }
    } catch (err) {
      setGridLoader(false);
      let blocker = { ...error };
      blocker.tenantName = "Something Went Wrong";
      setError(blocker);
      console.error(err);
    }
  };

  const getReportingManager = async () => {
    const url = `${serviceUrl}/api/super/admin/reporting/managers`;
    try {
      let response = await axios.get(url, {
        headers: genericHeaders(),
      });
      if (response.status === 200) {
        let newData = response?.data?.data;
        setReportingManagers(newData);
      }
    } catch (err) {
      let blocker = { ...error };
      blocker.tenantName = "Something Went Wrong";
      setError(blocker);
      console.error(err);
    }
  };

  const getDepartment = async () => {
    const url = `${serviceUrl}/api/department/super/admin/departments`;
    try {
      let response = await axios.get(url, {
        headers: genericHeaders(),
      });
      if (response.status === 200) {
        let newData = response?.data?.data;
        setDepartment(newData);
      }
    } catch (err) {
      let blocker = { ...error };
      blocker.tenantName = "Something Went Wrong";
      setError(blocker);
      console.error(err);
    }
  };

  const getDesignation = async () => {
    const url = `${serviceUrl}/api/designation/super/admin/designations`;
    try {
      let response = await axios.get(url, {
        headers: genericHeaders(),
      });
      if (response.status === 200) {
        let newData = response?.data?.data;
        setDesignation(newData);
      }
    } catch (err) {
      let blocker = { ...error };
      blocker.tenantName = "Something Went Wrong";
      setError(blocker);
      console.error(err);
    }
  };

  const getWorkLocation = async () => {
    const url = `${serviceUrl}/api/workLocation/super/admin/worklocations`;
    try {
      let response = await axios.get(url, {
        headers: genericHeaders(),
      });
      if (response.status === 200) {
        let newData = response?.data?.data;
        setWorkLocation(newData);
      }
    } catch (err) {
      let blocker = { ...error };
      blocker.tenantName = "Something Went Wrong";
      setError(blocker);
      console.error(err);
    }
  };

  useEffect(() => {
    getEmployees();
  }, [selectedTenant, searchValue, paginationModel]);

  useEffect(() => {
    getTenants();
    getDepartment();
    getDesignation();
    getWorkLocation();
    getReportingManager();
          dispatch(setIsViewedEmployee(false));
  }, []);

  const handleEdit = (row) => {
    navigate(`/view/employee/${row.id}`);
    console.log(row);
  };

  const getReportingManagerName = (id) => {
    return reportingManagers.find((manager) => manager.id === id)?.name ?? "-";
  };

  const getDepartmentName = (id) => {
    return (
      department.find((department) => department._id === id)?.departmentName ??
      "-"
    );
  };
  const getDesignationName = (id) => {
    return (
      designation.find((designation) => designation._id === id)?.designation ??
      "-"
    );
  };

  const getWorkLocationName = (id) => {
    return (
      workLocation.find((workLocation) => workLocation._id === id)?.location ??
      "-"
    );
  };

  const columns = [
    {
      field: "employeeId",
      headerName: "ID",
      width: 150,
      renderCell: (params) => <span>{params?.row?.employeeId ?? "-"}</span>,
    },
    {
      field: "name",
      headerName: "Name",
      width: 150,
      renderCell: (params) => <span>{params?.row?.name ?? "-"}</span>,
    },
    {
      field: "professionalEmailId",
      headerName: "Email",
      width: 200,
      renderCell: (params) => (
        <span>{params?.row?.professionalEmailId ?? "-"}</span>
      ),
    },
    {
      field: "reportingManager",
      headerName: "Reporting Manager",
      type: "number",
      renderCell: (params) => (
        <span>
          {getReportingManagerName(params?.row?.reportingManager) ?? "-"}
        </span>
      ),
    },
    {
      field: "designation",
      headerName: "Designation",
      renderCell: (params) => (
        <span>{getDesignationName(params?.row?.designation)}</span>
      ),
    },
    {
      field: "department",
      headerName: "Department",
      renderCell: (params) => (
        <span>{getDepartmentName(params?.row?.department)}</span>
      ),
    },
    {
      field: "workLocation",
      headerName: "Work Location",
      renderCell: (params) => (
        <span>{getWorkLocationName(params?.row?.workLocation)}</span>
      ),
    },
    {
      field: "role",
      headerName: "Role",
      renderCell: (params) => <span>{getIcon(params?.row?.role) ?? "-"}</span>,
    },
    {
      field: "edit",
      headerName: "Edit",
      renderCell: (params) => (
        <span className="cursor-pointer" onClick={() => handleEdit(params.row)}>
          {" "}
          <VisibilityIcon />{" "}
        </span>
      ),
    },
  ];

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
  };

   const getIcon = (user) => {
     let icon = `fad fa-check-circle`;
     switch (user) {
       case "ADMIN":
         icon = "fas fa-user-tie";
         break;
       case "EMPLOYEE":
         icon = "fas fa-user-graduate";
         break;
       case "MANAGER":
         icon = "fas fa-user-plus";
         break;
     }
     return (
       <div style={{ color: "#515151", fontSize: "17pt", margin: "0px 7px" }}>
         <Tooltip title={user}>
           <i className={icon} />
         </Tooltip>
       </div>
     );
   };

  return (
    <>
      <div className="flex items-center p-6 gap-6 w-full">
        <div className="w-[30%]">
          <p className="mb-2">Search</p>
          <input
            type="text"
            placeholder="Search by name & email"
            className="w-full mb-4 border border-black rounded-lg p-2 flex justify-end"
            value={searchValue}
            onChange={handleSearch}
          />
        </div>
        <div>
          <div className="flex justify-end items-end flex-col w-full">
            <p className="mb-2 text-left w-[80%]">Tenant</p>
            <select
              value={selectedTenant}
              onChange={(e) => {
                setPaginationModel({
                  page: 0,
                  pageSize: 10,
                })
                setSelectedTenant(e.target.value)
              }}
              className="w-[80%] mb-4 border border-black rounded-lg p-2"
            >
              <option value={""}>All Tenants</option>
              {tenants?.data?.map((tenant) => {
                return (
                  <option key={tenant.id} value={tenant.tenantid}>
                    {tenant.name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>
      <div className="px-6 pb-6 w-full">
        <DataGrid
          rows={employees?.data || []}
          columns={columns}
          loading={gridLoader}
          disableColumnMenu
          onPaginationModelChange={(newPaginationModel) =>
            setPaginationModel(newPaginationModel)
          }
          paginationModel={paginationModel}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10, 20, 50, 75, 100]}
          paginationMode="server"
          rowCount={employees?.totalcount || 0}
        />
      </div>
    </>
  );
};

export default EmployeeData;
